<template>
  <div v-show="showThisCell">
    <div
      class="canvas-wrapper"
      :class="{
        unclassified: unclassifiedState,
        selected: selectedState,
      }"
      @click="changeMark"
      @contextmenu.prevent.stop="handleRightClick"
      @mouseover="handleMouseOver"
      @mouseout="handleMouseOut"
    >
      <img
        ref="imgElement"
        :src="imgSrc"
        :width="thisImgWidth"
        :height="thisImgWidth"
        :alt="'Failed to load image'"
        :blank-height="153.6"
        decoding="async"
        loading="lazy"
        @load="handleImageLoad"
        @error="imgError"
      >

      <div
        v-if="shouldRenderStage"
        ref="konvaRoot"
        class="canvas-overlay"
        :style="{ width: thisImgWidth + 'px', height: thisImgWidth + 'px' }"
      />
    </div>
    <div class="tile-info">
      #{{ tileId }} {{ localCellTag }}
    </div>
  </div>
</template>

<script lang="ts" setup>
import { ref, computed, watch, onMounted, onBeforeUnmount, nextTick } from 'vue';
import { useIntersectionObserver } from '@vueuse/core';
import Konva from 'konva';
import { useConfigStore } from '@/store/config';
import { useCellGroupStore } from '@/store';
import type { CellBoundingBox } from '@/types/api';
import type { CheckboxValue } from 'bootstrap-vue-next';
import { useApi, useConstants } from "@/composables/useAppGlobals";
import { useCellVisibility } from '@/composables/useCellVisibility';
import { useIntersectionHandler } from '@/composables/useIntersectionHandler';
import { useCellImageCache } from '@/composables/useCellImageCache';

const props = withDefaults(defineProps<{
  isZPressing?: boolean;
  tileId: number;
  cellType: string;
  cellTag?: string;
  imgPath?: string;
  imgWidth: number;
  bbox?: number[][];
  showBbox?: boolean | CheckboxValue | CheckboxValue[];
  drawingMode?: boolean | CheckboxValue | CheckboxValue[];
  showNormal?: boolean | CheckboxValue | CheckboxValue[];
  showWrong?: boolean | CheckboxValue | CheckboxValue[];
  showExcluded?: boolean | CheckboxValue | CheckboxValue[];
}>(), {
  isZPressing: false,
  cellTag: '',
  imgPath: '',
  bbox: () => [],
  showBbox: false,
  drawingMode: false,
  showNormal: true,
  showWrong: true,
  showExcluded: true,
});

const emit = defineEmits(['activate', 'cellRightClick']);

const $api = useApi();
const $const = useConstants();
const config = useConfigStore();
const cellGroups = useCellGroupStore();
const { getCachedUrl } = useCellImageCache();

const CELL_DEFAULT_SIZE = $const?.CELL_DEFAULT_SIZE ?? 153.6;
const DEFAULT_CELL_IMG_SIZE = $const?.DEFAULT_CELL_IMG_SIZE ?? CELL_DEFAULT_SIZE;
const UNCLASSIFIED_TAG = $const?.CELL_TAGS?.UNCLASSIFIED ?? 'UNCLASSIFIED';
const SELECTED_TAG = $const?.CELL_TAGS?.SELECTED ?? 'SELECTED';

function isReviewState(state?: string): boolean {
  if (!$const || !state) return false;
  return state === $const.TASK_STATES.RELABELING || state === $const.TASK_STATES.FINAL_REVIEW;
}

type NormalizedBox = {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
};

type BBox = [number, number, number, number];

type StageRect = {
  x: number;
  y: number;
  width: number;
  height: number;
};

type RectDrawable = StageRect & {
  stroke: string;
  strokeWidth: number;
  id?: string;
};

function clamp(value: number, min: number, max: number): number {
  if (Number.isNaN(value)) return min;
  return Math.min(Math.max(value, min), max);
}

function normalizeBox(points: BBox | undefined): NormalizedBox | null {
  if (!Array.isArray(points) || points.length < 4) return null;
  const [x1, y1, x2, y2] = points;
  const minX = Math.min(x1, x2);
  const minY = Math.min(y1, y2);
  const maxX = Math.max(x1, x2);
  const maxY = Math.max(y1, y2);
  if (!Number.isFinite(minX) || !Number.isFinite(minY) || !Number.isFinite(maxX) || !Number.isFinite(maxY)) {
    return null;
  }
  if (maxX === minX || maxY === minY) {
    return null;
  }
  return { minX, minY, maxX, maxY };
}

function stageRectFromRaw(points: BBox | undefined, scale: number): StageRect | null {
  const normalized = normalizeBox(points);
  if (!normalized) return null;
  return {
    x: normalized.minX * scale,
    y: normalized.minY * scale,
    width: (normalized.maxX - normalized.minX) * scale,
    height: (normalized.maxY - normalized.minY) * scale,
  };
}

function rawBoxFromStage(points: BBox | undefined, scale: number, limit: number): BBox | null {
  const normalized = normalizeBox(points);
  if (!normalized || scale === 0) return null;
  const minX = clamp(normalized.minX / scale, 0, limit);
  const minY = clamp(normalized.minY / scale, 0, limit);
  const maxX = clamp(normalized.maxX / scale, 0, limit);
  const maxY = clamp(normalized.maxY / scale, 0, limit);
  if (maxX === minX || maxY === minY) return null;
  return [minX, minY, maxX, maxY];
}

const isMouseOver = ref(false);
const isMarked = ref(false);
const imgElement = ref<HTMLImageElement | null>(null);
const imgSrc = ref('');
const localCellTag = ref('');
const originCellTag = ref('');
const isDragging = ref(false);
const customBbox = ref<BBox | []>([]);
// removed unused labelChecked
const baseImgWidth = ref<number>(CELL_DEFAULT_SIZE);
const loadRetry = ref(0);
const widthUpdateFrame = ref<number | null>(null);
let stopIntersectionObserver: (() => void) | null = null;
let cachedImgPath: string | undefined = undefined;
let imgRequestId = 0;

async function loadImageForPath(path: string, options: { resetRawSize: boolean }) {
  cachedImgPath = path;
  const requestId = ++imgRequestId;
  const rawUrl = $api.getCellUrls(path);
  if (options.resetRawSize) {
    rawImgSize.value = CELL_DEFAULT_SIZE;
  }
  try {
    const cachedUrl = await getCachedUrl(rawUrl);
    if (requestId === imgRequestId) {
      imgSrc.value = cachedUrl;
    }
  } catch {
    if (requestId === imgRequestId) {
      imgSrc.value = rawUrl;
    }
  }
}

// Use the visibility composable for cleaner observer handling
const { isVisible, rawImgSize, handleVisibilityChange } = useCellVisibility(
  imgElement,
  CELL_DEFAULT_SIZE
);

const konvaRoot = ref<HTMLDivElement | null>(null);
let stage: Konva.Stage | null = null;
let layer: Konva.Layer | null = null;

const showThisCell = computed(() => {
  const cellTag = localCellTag.value || effectiveCellTag.value || '';
  if (cellTag === UNCLASSIFIED_TAG && props.showWrong) {
    return true;
  }
  if (cellTag === '' && props.showNormal) {
    return true;
  }
  if (cellTag === SELECTED_TAG) {
    return true;
  }
  return false;
});

const unclassifiedState = computed(() => localCellTag.value === UNCLASSIFIED_TAG);
const selectedState = computed(() => localCellTag.value === SELECTED_TAG);

// Prefer canonical data from the Pinia store when available. This lets the
// component react to updates saved to the store without requiring parents to
// re-propagate props.
const storeCell = computed(() => {
  const arr = (cellGroups.groupDict?.[props.cellType] || []) as CellBoundingBox[];
  return arr.find((cell) => cell.tile_id === props.tileId) ?? null;
});

const effectiveBBoxes = computed(() => {
  const obj = storeCell.value as CellBoundingBox;
  if (obj?.bounding_boxes && Array.isArray(obj.bounding_boxes)) {
    return obj.bounding_boxes;
  }
  return Array.isArray(props.bbox) ? props.bbox : [];
});

const effectiveCellTag = computed(() => {
  const tag = (storeCell.value as CellBoundingBox)?.tag;
  return tag !== undefined ? String(tag) : (props.cellTag ?? '');
});

const hasDraftBbox = computed(() => customBbox.value.length >= 4);

const allRectConfigs = computed<RectDrawable[]>(() => {
  if (!props.showBbox || (props.drawingMode && hasDraftBbox.value)) {
    return [];
  }
  if (!Array.isArray(effectiveBBoxes.value) || effectiveBBoxes.value.length === 0) {
    return [];
  }
  const scale = thisImgWidth.value / rawImgSize.value;
  return effectiveBBoxes.value.reduce((rects: RectDrawable[], bbox, index) => {
    const rect = stageRectFromRaw(bbox as BBox, scale);
    if (rect) {
      rects.push({
        id: `bbox-${index}`,
        ...rect,
        stroke: "red",
        strokeWidth: 2,
      });
    }
    return rects;
  }, []);
});

const customRectConfig = computed<RectDrawable | null>(() => {
  if (!hasDraftBbox.value) return null;
  
  const scale = thisImgWidth.value / rawImgSize.value;
  const rect = stageRectFromRaw(customBbox.value as BBox, scale);
  
  return rect ? {
    ...rect,
    stroke: "green",
    strokeWidth: 2,
  } : null;
});

const shouldRenderStage = computed(() => {
  const hasContent = !!props.showBbox || !!props.drawingMode || hasDraftBbox.value;
  const shouldShow = isVisible.value || !!props.drawingMode || hasDraftBbox.value;
  return hasContent && shouldShow;
});

const thisImgWidth = computed({
  get: () =>
    props.isZPressing && isMouseOver.value ? DEFAULT_CELL_IMG_SIZE * 1.5 : baseImgWidth.value,
  set: (value: number) => {
    if (Number.isFinite(value)) {
      baseImgWidth.value = value;
    }
  },
});

function getCurrentSlideState(): string | undefined {
  const stateKey = $const?.SLIDE_KEYS?.STATE;
  if (!stateKey) return undefined;
  const value = config.slideInfo?.[stateKey];
  return typeof value === 'string' ? value : undefined;
}

function refreshDrawing() {
  if (!shouldRenderStage.value) {
    return;
  }
  // Ensure DOM updates are flushed before manipulating Konva
  nextTick(() => {
    if (!konvaRoot.value) return;
    if (!stage) {
      createStage();
      return;
    }
    syncStageDimensions();
    draw();
  });
}

watch(() => props.imgWidth, (width: number | undefined) => {
  if (width == null) return;
  if (widthUpdateFrame.value) {
    cancelAnimationFrame(widthUpdateFrame.value);
  }
  widthUpdateFrame.value = requestAnimationFrame(() => {
    baseImgWidth.value = width;
    widthUpdateFrame.value = null;
    refreshDrawing();
  });
});

watch(() => props.drawingMode, 
  (enabled: boolean | CheckboxValue | CheckboxValue[] | undefined) => {
    // `enabled` can be boolean or bootstrap checkbox values; coerce to boolean for checks
    const isEnabled = Array.isArray(enabled) ? enabled.length > 0 : Boolean(enabled);
    if (!isEnabled && hasDraftBbox.value) {
      customBbox.value = [];
      refreshDrawing();
    }
  }
);

watch(() => props.imgPath, async (path: string | undefined) => {
  // Skip if path is same as cached or empty
  if (!path || path === cachedImgPath) return;

  await loadImageForPath(path, { resetRawSize: true });
  getRawImgSize();
});

watch(rawImgSize, () => {
  config.setIsBusy(false);
  refreshDrawing();
});

watch(isMarked, (marked: boolean) => {  
  const currentState = getCurrentSlideState();
  if (isReviewState(currentState)) {
    localCellTag.value = marked ? SELECTED_TAG : originCellTag.value;
    return;
  }
  localCellTag.value = marked ? UNCLASSIFIED_TAG : '';
});

watch(effectiveCellTag, (cellTag: string | undefined) => {
  if (cellTag !== localCellTag.value) {
    originCellTag.value = cellTag || '';
    localCellTag.value = cellTag || '';
    try {
      const currentState = getCurrentSlideState();
      isMarked.value = originCellTag.value !== '' && !isReviewState(currentState);
    } catch (e) {
      console.error(e);
      isMarked.value = false;
    }
  }
});

watch(() => localCellTag.value, (cellTag: string) => {
  try {
    cellGroups.tagCell({ fromType: props.cellType, tileId: props.tileId, tag: cellTag });
  } catch (e) { console.error(e); }
});

watch(shouldRenderStage, (shouldRender: boolean) => {
  if (shouldRender) {
    refreshDrawing();
  } else {
    destroyStage();
  }
}, { immediate: true });

watch(effectiveBBoxes, () => {
  refreshDrawing();
}, { deep: true, immediate: true });

function imgError() {
  if (loadRetry.value > 3) return;
  loadRetry.value += 1;
  if (cachedImgPath) {
    imgSrc.value = $api.getCellUrls(cachedImgPath);
  }
}

function changeMark() {
  if (config.isReadOnly) return;
  if (!props.drawingMode) {
    isMarked.value = !isMarked.value;
  }
  emit('activate');
}

function getImage(imgSrcLocal: string) {
  return new Promise<HTMLImageElement>((resolve) => {
    const img = new Image();
    img.src = imgSrcLocal;
    img.onload = () => resolve(img);
  });
}

async function getRawImgSize() {
  if (rawImgSize.value !== CELL_DEFAULT_SIZE) return;
  const img = await getImage(imgSrc.value);
  const width = img.naturalWidth || img.width || CELL_DEFAULT_SIZE;
  rawImgSize.value = width;
}

function handleImageLoad(event: Event) {
  const target = event.target as HTMLImageElement | null;
  if (target?.naturalWidth) {
    rawImgSize.value = target.naturalWidth;
  }
  if (!isVisible.value) {
    isVisible.value = true;
  }
  refreshDrawing();
}

function handleRightClick(event: MouseEvent) {
  if (config.isReadOnly) return;
  emit('cellRightClick', event, props.cellType, props.tileId);
}

function getPointerPos() {
  if (!stage) return null;
  const pos = stage.getPointerPosition();
  return pos ? { x: pos.x, y: pos.y } : null;
}

function handleMouseDownKonva() {
  emit('activate');
  const pos = getPointerPos();
  if (!pos) return;
  if (!props.drawingMode || config.isReadOnly) return;
  isDragging.value = true;
  const scale = thisImgWidth.value / rawImgSize.value;
  const rawStart = rawBoxFromStage([pos.x, pos.y, pos.x + 1, pos.y + 1], scale, rawImgSize.value);
  if (rawStart) {
    customBbox.value = [rawStart[0], rawStart[1], rawStart[0] + 1, rawStart[1] + 1];
  }
  // Add global mouseup listener to handle clicks outside the image
  window.addEventListener('mouseup', handleMouseUpKonva);
  draw();
}

function handleMouseMoveKonva() {
  if (!isDragging.value || customBbox.value.length < 4) return;
  if (!props.drawingMode || config.isReadOnly) return;
  const pos = getPointerPos();
  if (!pos) return;
  const scale = thisImgWidth.value / rawImgSize.value;
  const rawEnd = rawBoxFromStage([pos.x, pos.y, pos.x + 1, pos.y + 1], scale, rawImgSize.value);
  if (rawEnd) {
    customBbox.value = [customBbox.value[0], customBbox.value[1], rawEnd[0], rawEnd[1]] as BBox;
  }
  draw();
}

function handleMouseUpKonva() {
  if (!isDragging.value || !props.drawingMode || config.isReadOnly) return;
  isDragging.value = false;
  
  // customBbox already stores raw coordinates, save directly
  if (customBbox.value.length >= 4) {
    cellGroups.changeBbox({ fromType: props.cellType, tileId: props.tileId, bboxes: [customBbox.value as BBox] });
  }   
  
  // Clear draft bbox immediately after saving
  customBbox.value = [];
  // Remove global mouseup listener
  window.removeEventListener('mouseup', handleMouseUpKonva);
  refreshDrawing();
}

// removed unused changeToUnclassfied

function handleMouseOver() {
  isMouseOver.value = true;
}

function handleMouseOut() {
  isMouseOver.value = false;
}

function clearLayer() {
  if (!layer) return;
  layer.destroyChildren();
}

function draw() {
  if (!layer) return;
  clearLayer();
  // draw server bboxes
  allRectConfigs.value.forEach((cfg) => {
    const rect = new Konva.Rect({
      x: cfg.x,
      y: cfg.y,
      width: cfg.width,
      height: cfg.height,
      stroke: cfg.stroke,
      strokeWidth: cfg.strokeWidth,
    });
    layer?.add(rect);
  });
  // draw custom bbox
  const ccfg = customRectConfig.value;
  if (ccfg) {
    const rect = new Konva.Rect({
      x: ccfg.x,
      y: ccfg.y,
      width: ccfg.width,
      height: ccfg.height,
      stroke: ccfg.stroke,
      strokeWidth: ccfg.strokeWidth,
    });
    layer.add(rect);
  }
  layer.draw();
}

function syncStageDimensions() {
  if (!stage) return;
  stage.width(thisImgWidth.value);
  stage.height(thisImgWidth.value);
  layer?.draw();
}

function createStage() {
  if (stage || !konvaRoot.value) return;
  stage = new Konva.Stage({ container: konvaRoot.value, width: thisImgWidth.value, height: thisImgWidth.value });
  layer = new Konva.Layer();
  stage.add(layer);
  stage.on('mousedown', handleMouseDownKonva);
  stage.on('mousemove', handleMouseMoveKonva);
  stage.on('mouseup', handleMouseUpKonva);
  draw();
}

function destroyStage() {
  if (!stage) return;
  stage.off('mousedown', handleMouseDownKonva);
  stage.off('mousemove', handleMouseMoveKonva);
  stage.off('mouseup', handleMouseUpKonva);
  stage.destroy();
  stage = null;
  layer = null;
}

const interactionHandler = useIntersectionHandler(
  rawImgSize,
  handleVisibilityChange,
  getRawImgSize,
  refreshDrawing,
  CELL_DEFAULT_SIZE
);

onMounted(async () => {
  if (props.imgPath && props.imgPath !== cachedImgPath) {
    await loadImageForPath(props.imgPath, { resetRawSize: false });
  }
  baseImgWidth.value = props.imgWidth;

  // Initialize local tag from effective (store-backed) tag if available
  const initTag = effectiveCellTag.value !== SELECTED_TAG ? effectiveCellTag.value || '' : UNCLASSIFIED_TAG;
  localCellTag.value = initTag;
  originCellTag.value = initTag;

  const currentState = getCurrentSlideState();
  isMarked.value = originCellTag.value !== '' && !isReviewState(currentState);

  // Setup intersection observer for visibility detection
  const { stop } = useIntersectionObserver(
    imgElement,
    interactionHandler,
  );
  
  // Store the stop function to cleanup on unmount
  stopIntersectionObserver = stop;
});

onBeforeUnmount(() => {
  if (widthUpdateFrame.value) {
    cancelAnimationFrame(widthUpdateFrame.value);
    widthUpdateFrame.value = null;
  }
  // Clean up intersection observer
  if (stopIntersectionObserver) {
    stopIntersectionObserver();
    stopIntersectionObserver = null;
  }
  // Remove global mouseup listener if still attached
  window.removeEventListener('mouseup', handleMouseUpKonva);
  destroyStage();
});
</script>
<style lang="scss" scoped>
@use "@/assets/scss/common/alignment" as *;
.canvas-wrapper {
  margin: 20px;
  position: relative;
  &.unclassified {
    box-shadow: 0px 0px 10px 0px rgba(255, 0, 0, 0.836);
  }
  &.selected {
    box-shadow: 0px 0px 10px 0px rgb(0, 140, 255);
  }
  &:hover {
    cursor:pointer
  }
}
.canvas-overlay{
  position: absolute;
  top: 0;
  left: 0;
}
.tile-info{
  text-align: center;
  border-bottom: 2px solid #00000059;
  padding-bottom: 3px;
}
</style>
